import React, {useState} from 'react';
import {Pattern} from "./components/Pattern";
import {PatternControls} from "./components/PatternControls";
import {Colors} from "./components/Colors";
import {
    Alert,
    Button,
    CssBaseline,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar
} from "@mui/material";
import * as Chance from "chance";
import flowers from "flower-names";

import './App.css';
import logo from './logo192.png';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import {PrintView} from "./components/PrintView";
import TextField from "@mui/material/TextField";

export const max_size = 30;
export const max_increase = 40;
export const max_height = 40;
export const max_repeats = 10;

export const initial_size = 20;
export const initial_increase = 9;
export const initial_height = 12;
export const initial_repeats = 1;

export const color_transparent = { id: 0, color: "#ffffff00", name: "Transparent" };

function initPattern(increase, height, repeats, tiles) {
    let pattern = [];
    for (let i = 0; i < repeats; i++) {
        let segment = [];
        for (let j = 0; j < increase * 2 + height; j++) {
            let line = [];
            for (let k = 0; k < increase + 1; k++) {
                line.push(tiles && tiles[i] && tiles[i][j] ? tiles[i][j][k] || 0 : 0);
            }
            segment.push(line);
        }
        pattern.push(segment);
    }
    return pattern;
}

const App = () => {
    const chance = new Chance();

    const [state, setState] = useState({
        increase: initial_increase,
        size: initial_size,
        height: initial_height,
        repeats: initial_repeats,
        left: false,
        tiles: initPattern(max_increase, max_height, max_repeats),
        editedColor: null,
        colors: [
            { id: 1, color: "#ff0000", name: "Rot" },
            { id: 2, color: "#00ff00", name: "Grün" },
            { id: 3, color: "#0000ff", name: "Blau" },
        ],
        selectedColor: { id: 1, color: "#ff0000", name: "Rot" },
        print: false,
        isResetDialogOpen: false,
        isSaveDialogOpen: false,
        isLoadDialogOpen: false,
        dialogPatternId: '',
        errorMessage: '',
    });

    const {
        increase,
        size,
        height,
        repeats,
        left,
        tiles,
        editedColor,
        colors,
        selectedColor,
        print,
        isResetDialogOpen,
        isSaveDialogOpen,
        isLoadDialogOpen,
        dialogPatternId,
        errorMessage,
    } = state;

    const updateTile = (k, s, l, t) => {
        console.log(k, s, l, t);
        let newTiles = [...tiles];
        newTiles[s][l][t] = k === 0 ? 0 : selectedColor.id;
        setState({ ...state, tiles: newTiles });
    };

    const onColorAdd = () => setState({
        ...state,
        colors: [
            ...colors,
            {
                id: colors.length + 1,
                color: chance.color(),
                name: flowers.random(),
            },
        ],
    });

    const onColorDelete = c => {
        // delete color from tiles
        const newTiles = tiles.map(segment =>
            segment.map(line => line.map((k) => (k === c.id ? 0 : (k > c.id ? k - 1 : k)))));
        // reset selected color
        let newSelectedColor = selectedColor;
        if (selectedColor.id === c.id) {
            newSelectedColor = color_transparent;
        } else if (selectedColor.id > c.id) {
            newSelectedColor = colors.find((e) => e.id === selectedColor.id - 1);
        }
        // remove color from colors
        let newColors = colors.filter((e) => e.id !== c.id);
        // remove gap in color ids
        for (let i = c.id - 1; i < newColors.length; i++) {
            newColors[i].id--;
        }
        setState({...state, colors: newColors, tiles: newTiles, selectedColor: newSelectedColor});
    };

    const onPrint = () => {
        setState({...state, print: true});
        // setTimeout(() => window.print(), 10);
        // setTimeout(() => setState({...state, print: false}), 10);
    };

    const onSave = async () => {
        try {
            const data = {
                increase: increase,
                size: size,
                height: height,
                repeats: repeats,
                left: left,
                tiles: tiles,
                colors: colors
            };

            const response = await fetch("https://perlen.stieber-net.de/pattern", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                setState({...state, errorMessage: `Fehler beim Speichern des Musters: ${response.statusText}`});
            }

            const patternId = await response.text();

            console.log('Pattern saved successfully. Pattern ID:', patternId);
            setState({ ...state, dialogPatternId: patternId, isSaveDialogOpen: true });
        } catch (error) {
            setState({...state, errorMessage: `Fehler beim Speichern des Musters: ${error.message}`})
        }
    };

    const onLoad = async (patternId) => {
        try {
            const response = await fetch(`https://perlen.stieber-net.de/pattern/${patternId}`);
            if (!response.ok) {
                setState({...state, errorMessage: `Fehler beim Laden des Musters: ${response.statusText}`, isLoadDialogOpen: false});
            }
            const data = await response.json();
            setState({
                ...state,
                increase: data.increase,
                size: data.size,
                height: data.height,
                repeats: data.repeats,
                left: data.left,
                tiles: initPattern(max_increase, max_height, max_repeats, data.tiles),
                colors: data.colors,
                isLoadDialogOpen: false
            });
        } catch (error) {
            setState({...state, errorMessage: `Fehler beim Laden des Musters: ${error.message}`, isLoadDialogOpen: false});
        }
    };

    // load pattern if pattern id is given in url
    const urlParams = new URLSearchParams(window.location.search);
    const patternId = urlParams.get('pattern');
    if (patternId) {
        onLoad(patternId);
    }


    return (
        <div className="App">
            <React.Fragment>
                <CssBaseline />
                {!print && (
                    <div className="layout">
                        <div className="app-icon">
                            <img src={logo} height="80" alt="logo" />
                            <span>Perlen-Designer</span>
                        </div>
                        <PatternControls
                            size={size}
                            onSizeChange={(e, v) => setState({ ...state, size: v })}
                            increase={increase}
                            onIncreaseChange={(e, v) => setState({ ...state, increase: v })}
                            height={height}
                            onHeightChange={(e, v) => setState({ ...state, height: v })}
                            repeats={repeats}
                            onRepeatsChange={(e, v) => setState({ ...state, repeats: v })}
                            left={left}
                            onLeftChange={(e, v) => setState({ ...state, left: v })}
                            onReset={() => setState({ ...state, isResetDialogOpen: true })}
                            onPrint={onPrint}
                            onSave={onSave}
                            onLoad={() => setState({ ...state, isLoadDialogOpen: true })}
                        />
                        <div className="pattern-container">
                            <div className="sidebar">
                                <Colors
                                    colors={colors}
                                    onColorAdd={onColorAdd}
                                    onNameChange={(c, n) =>
                                        setState({
                                            ...state,
                                            colors: colors.map((e) => (e.id === c.id ? { ...c, name: n } : e)),
                                        })
                                    }
                                    onColorEdit={(c) => setState({ ...state, editedColor: c })}
                                    editedColor={editedColor}
                                    onColorChange={(c) =>
                                        setState({
                                            ...state,
                                            colors: colors.map((e) =>
                                                e.id === editedColor.id ? { ...e, color: c } : e
                                            ),
                                        })
                                    }
                                    onColorSelect={(c) => setState({ ...state, selectedColor: c })}
                                    selectedColor={selectedColor}
                                    onColorDelete={onColorDelete}
                                />
                            </div>
                            <div className="work-area">
                                <Pattern
                                    increase={increase}
                                    size={size}
                                    height={height}
                                    repeats={repeats}
                                    left={left}
                                    tiles={tiles}
                                    onTileClick={(k, s, l, t) => updateTile(k, s, l, t)}
                                    colors={colors.concat(color_transparent)}
                                    editable={true}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {print && <PrintView state={state} setState={setState} />}
            </React.Fragment>
            <Dialog open={isResetDialogOpen}>
                <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                    Muster zurücksetzen
                </DialogTitle>
                <DialogContent dividers>
                    <DialogContentText>
                        Wirklich alles löschen?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setState({
                                ...state,
                                tiles: initPattern(max_increase, max_height, max_repeats),
                                isResetDialogOpen: false,
                            });
                        }}
                    >
                        Ja
                    </Button>
                    <Button variant="outlined" onClick={() => setState({ ...state, isResetDialogOpen: false })}>
                        Nein
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isSaveDialogOpen} onClose={() => setState({ ...state, isSaveDialogOpen: false })}>
                <DialogTitle>Pattern ID</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Speicher dir die folgende Muster-ID, um das Muster später zu laden
                    </DialogContentText>
                    <DialogContentText>
                        {dialogPatternId}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setState({ ...state, isSaveDialogOpen: false })} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isLoadDialogOpen} onClose={() => setState({ ...state, isLoadDialogOpen: false })}>
                <DialogTitle>Muster laden</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Muster ID eingeben:
                    </DialogContentText>
                    <DialogContent>
                        <TextField
                            label="Pattern ID"
                            variant="outlined"
                            value={dialogPatternId}
                            onChange={(e) => setState({...state, dialogPatternId: e.target.value})}
                        />
                    </DialogContent>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => onLoad(dialogPatternId)}>
                        Muster Laden
                    </Button>
                    <Button variant="outlined" onClick={() => setState({ ...state, isLoadDialogOpen: false })}>
                        Abbrechen
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={errorMessage}
                autoHideDuration={6000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setState({...state, errorMessage: ''})}
            >
                <Alert onClose={() => setState({...state, errorMessage: ''})} severity="error">
                    {errorMessage}
                </Alert>
            </Snackbar>

        </div>
    );
};

export default App;